/**
 * Definición de rutas de la app — Sales Agent.
 *
 * Las páginas son placeholders mínimos que se implementarán en fases posteriores:
 *   Fase 2: Dashboard
 *   Fase 3: Tenants (list + detail + tabs + modals)
 *   Fase 4: Users (list + detail + permissions)
 *   Fase 5: Documents, Conversations, Quotations
 *   Fase 6: Audit Logs, Profile
 */
import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ProtectedRoute } from './ProtectedRoute';
import { LoadingSpinner } from '@/components/shared/feedback/Feedback';

const LoginPage = lazy(() => import('@/pages/public/LoginPage/LoginPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/public/ForgotPasswordPage/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/public/ResetPasswordPage/ResetPasswordPage'));
const DashboardPage = lazy(() => import('@/pages/app/dashboard/DashboardPage'));
const TenantList = lazy(() => import('@/pages/app/tenants/TenantListPage'));
const TenantDetail = lazy(() => import('@/pages/app/tenants/TenantDetailPage'));
const UserList = lazy(() => import('@/pages/app/users/UserListPage'));
const UserDetail = lazy(() => import('@/pages/app/users/UserDetailPage'));
const ProfilePage = lazy(() => import('@/pages/app/profile/ProfilePage'));
const AuditLogList = lazy(() => import('@/pages/app/audit/AuditLogListPage'));
const DocumentListPage = lazy(() => import('@/pages/app/documents/DocumentListPage'));
const ConversationListPage = lazy(() => import('@/pages/app/conversations/ConversationListPage'));
const ConversationDetailPage = lazy(() => import('@/pages/app/conversations/ConversationDetailPage'));
const QuotationListPage = lazy(() => import('@/pages/app/quotations/QuotationListPage'));
const QuotationDetailPage = lazy(() => import('@/pages/app/quotations/QuotationDetailPage'));
const SupplierListPage = lazy(() => import('@/pages/app/suppliers/SupplierListPage'));
const SupplierDetailPage = lazy(() => import('@/pages/app/suppliers/SupplierDetailPage'));
const PriceListItemsPage = lazy(() => import('@/pages/app/suppliers/PriceListItemsPage'));

function SuspenseBoundary({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<LoadingSpinner size="lg" />}>{children}</Suspense>;
}

export function AppRoutes() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<SuspenseBoundary><LoginPage /></SuspenseBoundary>} />
      <Route path="/forgot-password" element={<SuspenseBoundary><ForgotPasswordPage /></SuspenseBoundary>} />
      <Route path="/reset-password" element={<SuspenseBoundary><ResetPasswordPage /></SuspenseBoundary>} />

      {/* App (protected) */}
      <Route path="/app" element={<Navigate to="/app/dashboard" replace />} />

      {/* Fase 2: Dashboard */}
      <Route path="/app/dashboard" element={
        <ProtectedRoute crumbs={[{ label: 'Inicio', icon: 'home' }]}>
          <SuspenseBoundary><DashboardPage /></SuspenseBoundary>
        </ProtectedRoute>
      } />

      {/* Fase 3: Tenants */}
      <Route path="/app/tenants" element={
        <ProtectedRoute crumbs={[{ label: 'Inicio', to: '/app/dashboard', icon: 'home' }, { label: 'Tenants', icon: 'apartment' }]} requiredPermission="tenants.view">
          <SuspenseBoundary><TenantList /></SuspenseBoundary>
        </ProtectedRoute>
      } />
      <Route path="/app/tenants/new" element={
        <ProtectedRoute crumbs={[{ label: 'Inicio', to: '/app/dashboard', icon: 'home' }, { label: 'Tenants', to: '/app/tenants', icon: 'apartment' }, { label: 'Nuevo' }]} requiredPermission="tenants.create">
          <SuspenseBoundary><TenantDetail /></SuspenseBoundary>
        </ProtectedRoute>
      } />
      <Route path="/app/tenants/:tenantId" element={
        <ProtectedRoute crumbs={[{ label: 'Inicio', to: '/app/dashboard', icon: 'home' }, { label: 'Tenants', to: '/app/tenants', icon: 'apartment' }, { label: 'Detalle' }]} requiredPermission="tenants.view">
          <SuspenseBoundary><TenantDetail /></SuspenseBoundary>
        </ProtectedRoute>
      } />

      {/* Fase 4: Users */}
      <Route path="/app/users" element={
        <ProtectedRoute crumbs={[{ label: 'Inicio', to: '/app/dashboard', icon: 'home' }, { label: 'Usuarios', icon: 'group' }]} requiredPermission="users.view">
          <SuspenseBoundary><UserList /></SuspenseBoundary>
        </ProtectedRoute>
      } />
      <Route path="/app/users/new" element={
        <ProtectedRoute crumbs={[{ label: 'Inicio', to: '/app/dashboard', icon: 'home' }, { label: 'Usuarios', to: '/app/users', icon: 'group' }, { label: 'Nuevo' }]} requiredPermission="users.create">
          <SuspenseBoundary><UserDetail /></SuspenseBoundary>
        </ProtectedRoute>
      } />
      <Route path="/app/users/:userId" element={
        <ProtectedRoute crumbs={[{ label: 'Inicio', to: '/app/dashboard', icon: 'home' }, { label: 'Usuarios', to: '/app/users', icon: 'group' }, { label: 'Detalle' }]} requiredPermission="users.view">
          <SuspenseBoundary><UserDetail /></SuspenseBoundary>
        </ProtectedRoute>
      } />

      {/* Fase 5: Documents */}
      <Route path="/app/documents" element={
        <ProtectedRoute crumbs={[{ label: 'Inicio', to: '/app/dashboard', icon: 'home' }, { label: 'Documentos', icon: 'description' }]} requiredPermission="documents.view">
          <SuspenseBoundary><DocumentListPage /></SuspenseBoundary>
        </ProtectedRoute>
      } />

      {/* Fase 5: Conversations */}
      <Route path="/app/conversations" element={
        <ProtectedRoute crumbs={[{ label: 'Inicio', to: '/app/dashboard', icon: 'home' }, { label: 'Conversaciones', icon: 'chat' }]} requiredPermission="conversations.view">
          <SuspenseBoundary><ConversationListPage /></SuspenseBoundary>
        </ProtectedRoute>
      } />
      <Route path="/app/conversations/:conversationId" element={
        <ProtectedRoute crumbs={[{ label: 'Inicio', to: '/app/dashboard', icon: 'home' }, { label: 'Conversaciones', to: '/app/conversations', icon: 'chat' }, { label: 'Detalle' }]} requiredPermission="conversations.view">
          <SuspenseBoundary><ConversationDetailPage /></SuspenseBoundary>
        </ProtectedRoute>
      } />

      {/* Fase 5: Quotations */}
      <Route path="/app/quotations" element={
        <ProtectedRoute crumbs={[{ label: 'Inicio', to: '/app/dashboard', icon: 'home' }, { label: 'Cotizaciones', icon: 'request_quote' }]} requiredPermission="quotations.view">
          <SuspenseBoundary><QuotationListPage /></SuspenseBoundary>
        </ProtectedRoute>
      } />
      <Route path="/app/quotations/:quotationId" element={
        <ProtectedRoute crumbs={[{ label: 'Inicio', to: '/app/dashboard', icon: 'home' }, { label: 'Cotizaciones', to: '/app/quotations', icon: 'request_quote' }, { label: 'Detalle' }]} requiredPermission="quotations.view">
          <SuspenseBoundary><QuotationDetailPage /></SuspenseBoundary>
        </ProtectedRoute>
      } />

      {/* Fase 6: Audit Logs */}
      <Route path="/app/audit-logs" element={
        <ProtectedRoute crumbs={[{ label: 'Inicio', to: '/app/dashboard', icon: 'home' }, { label: 'Auditoría', icon: 'history' }]} requiredPermission="audit_logs.view">
          <SuspenseBoundary><AuditLogList /></SuspenseBoundary>
        </ProtectedRoute>
      } />

      {/* Fase 6: Profile */}
      <Route path="/app/profile" element={
        <ProtectedRoute crumbs={[{ label: 'Inicio', to: '/app/dashboard', icon: 'home' }, { label: 'Mi perfil', icon: 'person' }]}>
          <SuspenseBoundary><ProfilePage /></SuspenseBoundary>
        </ProtectedRoute>
      } />

      {/* Proveedores */}
      <Route path="/app/suppliers" element={
        <ProtectedRoute crumbs={[{ label: 'Inicio', to: '/app/dashboard', icon: 'home' }, { label: 'Proveedores', icon: 'inventory_2' }]} requiredPermission="suppliers.view">
          <SuspenseBoundary><SupplierListPage /></SuspenseBoundary>
        </ProtectedRoute>
      } />
      <Route path="/app/suppliers/new" element={
        <ProtectedRoute crumbs={[{ label: 'Inicio', to: '/app/dashboard', icon: 'home' }, { label: 'Proveedores', to: '/app/suppliers', icon: 'inventory_2' }, { label: 'Nuevo' }]} requiredPermission="suppliers.create">
          <SuspenseBoundary><SupplierDetailPage /></SuspenseBoundary>
        </ProtectedRoute>
      } />
      <Route path="/app/suppliers/:supplierId" element={
        <ProtectedRoute crumbs={[{ label: 'Inicio', to: '/app/dashboard', icon: 'home' }, { label: 'Proveedores', to: '/app/suppliers', icon: 'inventory_2' }, { label: 'Detalle' }]} requiredPermission="suppliers.view">
          <SuspenseBoundary><SupplierDetailPage /></SuspenseBoundary>
        </ProtectedRoute>
      } />
      <Route path="/app/suppliers/:supplierId/price-lists/:listId/items" element={
        <ProtectedRoute crumbs={[{ label: 'Inicio', to: '/app/dashboard', icon: 'home' }, { label: 'Proveedores', to: '/app/suppliers', icon: 'inventory_2' }, { label: 'Items' }]} requiredPermission="supplier_price_lists.view">
          <SuspenseBoundary><PriceListItemsPage /></SuspenseBoundary>
        </ProtectedRoute>
      } />

      {/* Fallback */}
      <Route path="/" element={<Navigate to="/app/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/app/dashboard" replace />} />
    </Routes>
  );
}
